import { useState, useRef, useEffect, useCallback } from 'react';
import { EnrichedClaim } from './types';

interface EditClaimModalProps {
    isOpen: boolean;
    onClose: () => void;
    claim: EnrichedClaim | null;
    onSave: (claimId: string, data: { description: string; evidenceUrls: string[]; imageUrls: string[] }) => Promise<void>;
    onUploadFile?: (file: File) => Promise<{ url: string; key: string }>;
}

export function EditClaimModal({
    isOpen,
    onClose,
    claim,
    onSave,
    onUploadFile,
}: EditClaimModalProps) {
    const [description, setDescription] = useState('');
    const [referenceUrl, setReferenceUrl] = useState('');
    const [referenceUrls, setReferenceUrls] = useState<string[]>([]);
    const [imageUrls, setImageUrls] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const currentClaimIdRef = useRef<string | null>(null);
    const isOpenRef = useRef(false);
    const sessionIdRef = useRef(0);
    const blobUrlsRef = useRef<Set<string>>(new Set());
    const imageCountRef = useRef(0);
    const [isDragging, setIsDragging] = useState(false);

    // Pre-fill form when modal opens or claim changes
    useEffect(() => {
        if (isOpen && claim) {
            isOpenRef.current = true;
            sessionIdRef.current += 1;
            currentClaimIdRef.current = claim.id;
            imageCountRef.current = (claim.imageUrls || []).length;
            setDescription(claim.description || '');
            setReferenceUrls(claim.evidenceUrls || []);
            setImageUrls(claim.imageUrls || []);
            setReferenceUrl('');
            setError('');
        } else {
            isOpenRef.current = false;
            currentClaimIdRef.current = null;
            // Revoke all tracked blob URLs when modal closes
            blobUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
            blobUrlsRef.current.clear();
        }
    }, [isOpen, claim]);

    const uploadFile = useCallback(async (file: File) => {
        if (!file.type.startsWith('image/')) {
            setError('Please select an image file');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setError('Image must be less than 5MB');
            return;
        }

        // Reserve a slot synchronously after validation to prevent concurrent uploads exceeding limit
        if (imageCountRef.current >= 3) {
            setError('Maximum 3 images allowed');
            return;
        }
        imageCountRef.current += 1;

        const sessionAtStart = sessionIdRef.current;
        setError('');
        setUploading(true);

        try {
            if (onUploadFile) {
                const result = await onUploadFile(file);
                if (!isOpenRef.current || sessionIdRef.current !== sessionAtStart) {
                    imageCountRef.current -= 1;
                    return;
                }
                setImageUrls(prev => {
                    if (prev.length >= 3) {
                        imageCountRef.current -= 1;
                        return prev;
                    }
                    return [...prev, result.url];
                });
            } else {
                const url = URL.createObjectURL(file);
                if (!isOpenRef.current || sessionIdRef.current !== sessionAtStart) {
                    URL.revokeObjectURL(url);
                    imageCountRef.current -= 1;
                    return;
                }
                blobUrlsRef.current.add(url);
                setImageUrls(prev => {
                    if (prev.length >= 3) {
                        URL.revokeObjectURL(url);
                        blobUrlsRef.current.delete(url);
                        imageCountRef.current -= 1;
                        return prev;
                    }
                    return [...prev, url];
                });
            }
        } catch (err: unknown) {
            imageCountRef.current -= 1;
            if (isOpenRef.current && sessionIdRef.current === sessionAtStart) {
                const message = err instanceof Error ? err.message : 'Failed to upload image';
                setError(message);
            }
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    }, [onUploadFile]);

    // Global paste listener
    useEffect(() => {
        if (!isOpen) return;
        function onGlobalPaste(e: ClipboardEvent) {
            const target = e.target as HTMLElement;
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
            const items = e.clipboardData?.items as DataTransferItemList | undefined;
            if (!items) return;
            for (let i = 0; i < items.length; i++) {
                if (items[i].type.startsWith('image/')) {
                    const file = items[i].getAsFile();
                    if (file) {
                        uploadFile(file);
                        break;
                    }
                }
            }
        }
        document.addEventListener('paste', onGlobalPaste);
        return () => document.removeEventListener('paste', onGlobalPaste);
    }, [isOpen, uploadFile]);

    // Auto-close if claim status is no longer pending
    useEffect(() => {
        if (isOpen && claim && claim.status !== 'pending') {
            onClose();
        }
    }, [isOpen, claim, onClose]);

    if (!isOpen || !claim || claim.status !== 'pending') return null;

    function addReferenceUrl() {
        if (referenceUrl.trim()) {
            setReferenceUrls([...referenceUrls, referenceUrl.trim()]);
            setReferenceUrl('');
        }
    }

    function removeReferenceUrl(index: number) {
        setReferenceUrls(referenceUrls.filter((_, i) => i !== index));
    }

    async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        if (!file) return;
        await uploadFile(file);
    }

    function handleDrop(e: React.DragEvent) {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) uploadFile(file);
    }

    function removeImageUrl(index: number) {
        const url = imageUrls[index];
        if (url && url.startsWith('blob:')) {
            URL.revokeObjectURL(url);
            blobUrlsRef.current.delete(url);
        }
        const newUrls = imageUrls.filter((_, i) => i !== index);
        imageCountRef.current = newUrls.length;
        setImageUrls(newUrls);
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const finalReferenceUrls = referenceUrl.trim()
                ? [...referenceUrls, referenceUrl.trim()]
                : referenceUrls;

            await onSave(claim!.id, {
                description,
                evidenceUrls: finalReferenceUrls,
                imageUrls,
            });

            onClose();
        } catch (err: unknown) {
            const message = err instanceof Error
                ? err.message
                : typeof err === 'string' ? err : 'Failed to save changes';
            setError(message);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
            <div className="bg-surface rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto border border-border">
                <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                        <h2 className="text-2xl font-bold text-text-primary">
                            Edit Claim
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-text-secondary hover:text-text-primary"
                        >
                            ✕
                        </button>
                    </div>

                    <div className="mb-6 p-4 bg-background rounded-lg border border-border">
                        <h3 className="font-semibold text-text-primary mb-1">{claim.itemTitle}</h3>
                        <p className="text-text-secondary text-sm">{claim.gameName}</p>
                    </div>

                    <form onSubmit={handleSubmit}>
                        {error && (
                            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 text-red-400 rounded">
                                {error}
                            </div>
                        )}

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                                Description / Proof
                            </label>
                            <textarea
                                required
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-surface text-text-primary"
                                rows={4}
                                placeholder="Describe how you completed this item..."
                            />
                        </div>

                        {/* Reference Image Section */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                                📷 Reference Image (up to 3)
                            </label>
                            {imageUrls.length < 3 && (
                                <div
                                    className={`mb-2 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                                        isDragging
                                            ? 'border-primary bg-primary/5'
                                            : 'border-border hover:border-primary hover:bg-surface'
                                    } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                                    onClick={() => fileInputRef.current?.click()}
                                    onDrop={handleDrop}
                                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                                    onDragLeave={() => setIsDragging(false)}
                                    tabIndex={0}
                                >
                                    <div className="text-2xl mb-1">📎</div>
                                    <p className="text-sm text-text-secondary">
                                        <span className="font-semibold text-primary">Click to upload</span>, drag &amp; drop,
                                    </p>
                                    <p className="text-sm text-text-secondary">
                                        or <span className="font-semibold text-primary">paste</span> a screenshot (⌘V / Ctrl+V)
                                    </p>
                                    {uploading && (
                                        <p className="text-xs text-primary mt-1">Uploading...</p>
                                    )}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileUpload}
                                        className="hidden"
                                    />
                                </div>
                            )}
                            {imageUrls.length > 0 && (
                                <div className="grid grid-cols-3 gap-2">
                                    {imageUrls.map((url, index) => (
                                        <div key={index} className="relative group">
                                            <img
                                                src={url}
                                                alt={`Reference ${index + 1}`}
                                                className="w-full h-24 object-cover rounded-lg border border-border"
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23333" width="100" height="100"/><text fill="%23999" font-size="12" x="50%" y="50%" text-anchor="middle">Invalid</text></svg>';
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeImageUrl(index)}
                                                className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <p className="text-xs text-text-muted mt-1">
                                {imageUrls.length}/3 images added • Max 5MB per image
                            </p>
                        </div>

                        {/* Reference URLs */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                                🔗 Reference URL (Optional)
                            </label>
                            <p className="text-xs text-text-muted mb-2">
                                e.g., PR link, Slack message, Jira ticket, etc.
                            </p>
                            <div className="flex gap-2 mb-2">
                                <input
                                    type="url"
                                    value={referenceUrl}
                                    onChange={(e) => setReferenceUrl(e.target.value)}
                                    className="flex-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-surface text-text-primary"
                                    placeholder="https://..."
                                />
                                <button
                                    type="button"
                                    onClick={addReferenceUrl}
                                    className="px-4 py-2 bg-background text-text-secondary rounded-lg hover:bg-border border border-border"
                                >
                                    Add
                                </button>
                            </div>
                            {referenceUrls.length > 0 && (
                                <ul className="space-y-1">
                                    {referenceUrls.map((url: string, index: number) => (
                                        <li
                                            key={index}
                                            className="flex items-center gap-2 text-sm bg-background p-2 rounded border border-border"
                                        >
                                            <a
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex-1 text-primary hover:underline truncate"
                                            >
                                                {url}
                                            </a>
                                            <button
                                                type="button"
                                                onClick={() => removeReferenceUrl(index)}
                                                className="text-red-400 hover:text-red-300"
                                            >
                                                Remove
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div className="flex gap-3 justify-end">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 border border-border rounded-md text-text-secondary hover:bg-background"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading || uploading || !description.trim()}
                                className="btn-primary"
                            >
                                {loading ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
